import React, { useMemo } from "react";
import { useWatchlist } from "features/watchlist";
import { useNavigateToTitle } from "../../hooks/useNavigateToTitle";
import { ReleaseCardModel } from "./HomeLandingModel";
import { releaseCardToAsset } from "../../mappers/releaseCardToAsset";
import RoleChip from "./RoleChip";

type ReleaseCardProps = {
  item: ReleaseCardModel;
  /**
   * Watchlist page: handle the watchlist button via row id (e.g. `removeByRowId`)
   * instead of `toggleWatchlist` (needed when `catalogAssetId` is missing).
   */
  onWatchlistButtonClick?: () => void;
};

export default function ReleaseCard({
  item,
  onWatchlistButtonClick,
}: ReleaseCardProps) {
  const watchlist = useWatchlist();
  const navigateToTitle = useNavigateToTitle();
  const asset = useMemo(() => releaseCardToAsset(item), [item]);
  const saved =
    onWatchlistButtonClick != null
      ? true
      : watchlist.enabled && watchlist.isInWatchlist(asset.id);

  const metaParts = item.picks
    .map((p) => p.value)
    .filter(Boolean)
    .join(" • ");

  const watchlistLabel =
    onWatchlistButtonClick != null
      ? "Remove from watchlist"
      : saved
        ? "Remove from watchlist"
        : "Add to watchlist";

  const openTitle = () => {
    navigateToTitle(asset.id);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openTitle();
    }
  };

  const handleWatchlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onWatchlistButtonClick) {
      onWatchlistButtonClick();
      return;
    }
    void watchlist.toggleWatchlist(asset);
  };

  const handleWatchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openTitle();
  };

  return (
    <div
      className="homeLandingFeaturedCard"
      role="button"
      tabIndex={0}
      onClick={openTitle}
      onKeyDown={handleCardKeyDown}
      aria-label={`Open ${item.title}`}
    >
      <div className="homeLandingFeaturedCardImageWrap">
        {item.thumbnailSrc ? (
          <img
            className="homeLandingFeaturedCardImage"
            src={item.thumbnailSrc}
            alt={item.title}
          />
        ) : (
          <div className="homeLandingFeaturedCardImagePlaceholder" />
        )}
        <div className="homeLandingFeaturedCardChipOverlay">
          <RoleChip role={item.role} />
        </div>
      </div>

      <div className="homeLandingFeaturedCardBody">
        <div className="homeLandingFeaturedCardTitleRow">
          <h2 className="homeLandingFeaturedCardTitle">{item.title}</h2>
          <div className="homeLandingFeaturedCardButtons">
            {watchlist.enabled || onWatchlistButtonClick ? (
              <button
                type="button"
                className={[
                  "homeLandingFeaturedCardBtnSecondary",
                  saved ? "homeLandingFeaturedCardBtnWatchlistActive" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={handleWatchlistClick}
                aria-label={watchlistLabel}
              >
                {watchlistLabel}
              </button>
            ) : null}
            <button
              type="button"
              className="homeLandingFeaturedCardBtnPrimary"
              onClick={handleWatchClick}
              aria-label={`Watch ${item.title}`}
            >
              Watch
            </button>
          </div>
        </div>

        {metaParts && (
          <div className="homeLandingFeaturedCardMeta">{metaParts}</div>
        )}

        {(item.description || item.subtitle) && (
          <p className="homeLandingFeaturedCardDescription">
            {item.description ?? item.subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
